import { isNumber } from '@common/util/types';
import { getUuid } from '@common/util/uuid';
import { curveLinear } from '@visx/curve';
import { LinearGradient } from '@visx/gradient';
import { LinePathConfig, PositionScale } from '@visx/shape/lib/types';
import chroma from 'chroma-js';
import { memo, useMemo } from 'react';
import AreaClosed from './shapes/AreaClosed';
import LinePath from './shapes/LinePath';

export type EntityLineProps<D> = Omit<LinePathConfig<D>, 'x' | 'y'> & {
    x: (d: D) => number;
    y: (d: D) => number;
    data: D[];
    color: string;
    yScale: PositionScale;
    solid?: boolean;
    strokeWidth?: number;
    gradientAlphaMax?: number;
};

function EntityLineInner<D>({
    x,
    y,
    data,
    color,
    yScale,
    solid,
    strokeWidth,
    gradientAlphaMax = 0.1,
}: EntityLineProps<D>) {
    const id = useMemo(() => getUuid(), []);
    const positiveGradientId = 'area-gradient-positive|' + id;
    const negativeGradientId = 'area-gradient-negative|' + id;
    const yDomain = yScale.domain();
    const yRange = yScale.range();

    return (
        <>
            <LinearGradient
                id={positiveGradientId}
                from={chroma(color).alpha(gradientAlphaMax).hex()}
                to={chroma(color).alpha(0).hex()}
            />
            <LinearGradient
                id={negativeGradientId}
                from={chroma(color).alpha(0).hex()}
                to={chroma(color).alpha(gradientAlphaMax).hex()}
            />
            {yDomain[0] < 0 ? (
                // If the minimum value of the yDomain is negative, draw an area from the line up to 0
                <AreaClosed
                    data={data}
                    x={x}
                    y0={(d) => {
                        // Bottom of area is just the line itself
                        return y(d);
                    }}
                    y1={() => {
                        // Top of area is either the Y-value of $0, or the top of the chart
                        return Math.max(yScale(0) ?? 0, yRange[1]);
                    }}
                    yScale={yScale}
                    strokeWidth={0}
                    stroke={`url(#${negativeGradientId})`}
                    fill={`url(#${negativeGradientId})`}
                    curve={curveLinear}
                />
            ) : (
                // If the minimum value of the yDomain is positive, draw an area from the line down to 0
                <AreaClosed
                    data={data}
                    x={x}
                    y0={(d) => {
                        // Top of area is just the line itself
                        return y(d);
                    }}
                    y1={() => {
                        // Bottom of area is either the Y-value of $0 or the bottom of the chart
                        return Math.min(yScale(0) ?? 0, yRange[0]);
                    }}
                    yScale={yScale}
                    strokeWidth={0}
                    stroke={`url(#${positiveGradientId})`}
                    fill={`url(#${positiveGradientId})`}
                    curve={curveLinear}
                />
            )}
            <LinePath
                data={data}
                x={x}
                y={y}
                curve={curveLinear}
                stroke={color}
                strokeWidth={
                    isNumber(strokeWidth) ? strokeWidth : solid ? 3 : 2
                }
                strokeDasharray={solid ? undefined : '1 6'}
                strokeLinecap="square"
            />
        </>
    );
}
export const EntityLine = memo(EntityLineInner, (prevProps, nextProps) => {
    return (
        prevProps.data === nextProps.data &&
        prevProps.color === nextProps.color &&
        prevProps.yScale === nextProps.yScale &&
        prevProps.x === nextProps.x &&
        prevProps.y === nextProps.y
    );
    // Required for generics to function correctly with memo()
}) as typeof EntityLineInner;
